import * as React from 'react';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import {
	getCurrency,
	getWorkOrderTimeZoneId,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { INVOICE_STATUS_DISPLAY_NAMES } from '../../constants/InvoiceStatuses';
import InvoiceDisplayStatusDisplay from '../invoice_display_status_display/InvoiceDisplayStatusDisplay';
import DateTimeHover from '../date_time_component/DateTime';
import { getContactName } from '../../utils/DataFormatterUtils';
import { getSanitisedWorkOrder } from '../../utils/WorkOrderUtils';
import {
	getInvoiceOtherCompanyEntity,
	getInvoiceSubmittedAt,
	getInvoiceSubmittedContact,
} from '../invoice_utilities/InvoiceUtilities';

require('./InvoiceRowDisplay.less');

export const InvoiceRowDisplay: React.FC<any> = ({ invoice, currentUser, ...props }) => {
	const wo = getSanitisedWorkOrder(nullSafeGetOrElse('workOrder', invoice, {}));
	// Project invoices have no work order at all, so the WO-derived title, location and
	// "WO #" line have nothing behind them. Such an invoice carries its own title instead
	// (AI-derived at upload, editable on the form). Falling back to the project's title
	// covers the ones created before that field existed — several invoices under one
	// project then read alike, which is exactly what the title was added to fix.
	const project = nullSafeGet('project', invoice);
	const title = project
		? nullSafeGetOrElse('title', invoice, nullSafeGet('title', project))
		: wo.title;
	const locationName = nullSafeGetOrElse(
		'location.name',
		wo,
		nullSafeGet('location.name', invoice)
	);
	const otherFacility = getInvoiceOtherCompanyEntity(invoice, currentUser);
	const submittedTime = getInvoiceSubmittedAt(invoice) || nullSafeGet('createdAt', invoice);
	const submittedContact = getInvoiceSubmittedContact(invoice) || invoice.createdByContact;

	return (
		<div className="invoiceRowDisplay" {...props}>
			<div className="rowColumn">
				<div className="rowPrimary" style={{ color: 'rgba(0,0,0,0.65)' }}>
					<Ellipsis
						tooltip={true}
						fullWidthRecognition={true}
						lines={1}
						style={{ paddingBottom: '1px' }}
					>
						{title}
					</Ellipsis>
				</div>
				<div className="rowSecondary">
					{locationName ? (
						<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
							<span className="rowGrayChip">
								<i className="icons8-font icons8-location-marker" />
								<span>{locationName}</span>
							</span>
						</div>
					) : null}
					<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
						<InvoiceDisplayStatusDisplay status={INVOICE_STATUS_DISPLAY_NAMES[invoice.status]} />
					</div>
					{nullSafeGet('isCapex', wo) ? (
						<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
							<span className={'rowGrayChip'} style={{ padding: '4px 12px', borderRadius: 16 }}>
								<span>CapEx</span>
							</span>
						</div>
					) : null}
					{nullSafeGet('problemType.hierarchyName', wo) ? (
						<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
							<i className="icons8-font icons8-maintenance-filled" />
							<span>{nullSafeGet('problemType.hierarchyName', wo)}</span>
						</div>
					) : null}
					{nullSafeGet('displayName', otherFacility) ? (
						<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
							<i className="icons8-font icons8-company" />
							{nullSafeGet('displayName', otherFacility)}
						</div>
					) : null}
					{nullSafeGet('glCode', invoice) ? (
						<div className="rowInlineGroup">
							{'GL Code: '.concat(nullSafeGet('glCode', invoice))}
						</div>
					) : null}
					<div className="rowInlineGroup">{`Invoice #${nullSafeGetOrElse(
						'invoiceNumber',
						invoice,
						'--'
					)}`}</div>
					{nullSafeGet('id', wo) ? (
						<div className="rowInlineGroup">{`WO #${nullSafeGet('id', wo)}`}</div>
					) : null}
					{/* A project invoice's row title is the invoice's own, so the project it belongs
					    to has to be named here — same chip the buyerProjects branches below use for
					    work-order invoices. */}
					{project ? (
						<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
							<span>
								<i className="icons8-font icons8-blueprint" />
								{nullSafeGet('title', project)}
							</span>
						</div>
					) : null}
					{nullSafeGet('buyerProjects.length', wo) === 1 ? (
						<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
							<span>
								<i className="icons8-font icons8-blueprint" />
								{nullSafeGet('buyerProjects', wo)[0].title}
							</span>
						</div>
					) : nullSafeGet('buyerProjects.length', wo) > 1 ? (
						<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
							<span>
								<i className="icons8-font icons8-blueprint" />
								{nullSafeGet('buyerProjects.length', wo)} Projects
							</span>
						</div>
					) : null}
				</div>
				<div className="rowTertiary rowBlackPrimary mt-2">
					<span style={{ whiteSpace: 'nowrap', color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
						<>
							Submitted{' '}
							<DateTimeHover
								timestamp={submittedTime}
								showTime={false}
								timeZone={getWorkOrderTimeZoneId(nullSafeGet('workOrder', invoice))}
							/>{' '}
							{submittedContact ? `by ${getContactName(submittedContact)}` : ''}
						</>
					</span>
				</div>
			</div>
			<div className="invoiceRowDisplay__rightSide">
				<div className="invoiceRowDisplay__rowInlineGroup">
					{getCurrency({
						workOrder: wo,
						supplierFacility: nullSafeGet('supplierFacility', invoice),
					}).format(invoice.invoiceTotalAfterTax)}
				</div>
			</div>
		</div>
	);
};
